import { useState, useCallback, useRef } from 'react'

// ── Types ──────────────────────────────────────────────────

export interface UserMessage {
  role: 'user'
  content: string
}

export interface AssistantMessage {
  role: 'assistant'
  content: string
  toolCalls: ToolCallEvent[]
}

export interface ErrorMessage {
  role: 'error'
  content: string
}

export interface ToolCallEvent {
  id: string
  name: string
  arguments: Record<string, unknown>
  result?: unknown
}

export type ChatMessage = UserMessage | AssistantMessage | ErrorMessage

type ChatStatus = 'idle' | 'streaming' | 'error'

// ── Hook ───────────────────────────────────────────────────

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [status, setStatus] = useState<ChatStatus>('idle')
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID())
  const abortRef = useRef<AbortController | null>(null)

  const send = useCallback(
    async (text: string) => {
      const userMsg: UserMessage = { role: 'user', content: text }
      setMessages((prev) => [...prev, userMsg])
      setStatus('streaming')

      // Prepare a streaming assistant message
      const assistantMsg: AssistantMessage = { role: 'assistant', content: '', toolCalls: [] }
      setMessages((prev) => [...prev, assistantMsg])

      const controller = new AbortController()
      abortRef.current = controller

      try {
        const res = await fetch('/api/ai/chat', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: text, sessionId }),
          signal: controller.signal,
        })

        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.error?.message || `Request failed (${res.status})`)
        }

        const reader = res.body!.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              // Store event type for next data line
              ;(reader as unknown as Record<string, string>).__lastEvent = line.slice(7).trim()
              continue
            }
            if (!line.startsWith('data: ')) continue

            const eventType = (reader as unknown as Record<string, string>).__lastEvent || 'text'
            const raw = line.slice(6).trim()
            if (!raw) continue

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let payload: Record<string, any>
            try {
              payload = JSON.parse(raw)
            } catch {
              continue
            }

            if (eventType === 'text') {
              assistantMsg.content += payload.content
              setMessages((prev) => {
                const next = [...prev]
                next[next.length - 1] = { ...assistantMsg }
                return next
              })
            } else if (eventType === 'tool_call') {
              assistantMsg.toolCalls.push({
                id: payload.id,
                name: payload.name,
                arguments: payload.arguments,
              })
              setMessages((prev) => {
                const next = [...prev]
                next[next.length - 1] = { ...assistantMsg, toolCalls: [...assistantMsg.toolCalls] }
                return next
              })
            } else if (eventType === 'tool_result') {
              const tc = assistantMsg.toolCalls.find((t) => t.id === payload.id)
              if (tc) tc.result = payload.result
              setMessages((prev) => {
                const next = [...prev]
                next[next.length - 1] = { ...assistantMsg, toolCalls: [...assistantMsg.toolCalls] }
                return next
              })
            } else if (eventType === 'error') {
              setMessages((prev) => [
                ...prev.slice(0, -1),
                { role: 'error', content: payload.message },
              ])
              setStatus('error')
              return
            } else if (eventType === 'done') {
              // done
            }
          }
        }

        setStatus('idle')
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          setStatus('idle')
          return
        }
        // Remove the empty assistant message and add error
        const message = err instanceof Error ? err.message : 'Something went wrong'
        setMessages((prev) => [...prev.slice(0, -1), { role: 'error', content: message }])
        setStatus('error')
      } finally {
        abortRef.current = null
      }
    },
    [sessionId],
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const newSession = useCallback(() => {
    setMessages([])
    setSessionId(crypto.randomUUID())
    setStatus('idle')
  }, [])

  const retry = useCallback(() => {
    // Find the last user message and resend
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user')
    if (!lastUserMsg) return
    // Remove everything after and including the last user message
    const lastIdx = messages.lastIndexOf(lastUserMsg)
    setMessages(messages.slice(0, lastIdx))
    setStatus('idle')
    // Resend
    send(lastUserMsg.content)
  }, [messages, send])

  return { messages, status, sessionId, send, stop, newSession, retry }
}
